<template>
  <v-container style="max-width: 700px;">
    <h1 class="mb-4">Send Feedback</h1>

    <v-card>
      <v-card-text>
        <v-form ref="formRef" v-model="formValid" @submit.prevent="submitFeedback">
          <v-select
            v-model="type"
            :items="typeOptions"
            label="Type"
            :rules="[v => !!v || 'Please select a type']"
            required
          />
          <v-text-field
            v-model="subject"
            label="Subject"
            :rules="[v => !!v.trim() || 'Subject is required']"
            required
            class="mt-2"
          />
          <v-textarea
            v-model="message"
            label="Message"
            rows="5"
            :rules="[v => !!v.trim() || 'Message is required']"
            required
            class="mt-2"
          />
          <div class="mt-4 d-flex align-center" style="gap: 16px;">
            <v-btn color="primary" type="submit" :loading="submitting" :disabled="!formValid">
              Submit Feedback
            </v-btn>
            <span v-if="successMessage" style="color: green;">{{ successMessage }}</span>
            <span v-if="errorMessage" style="color: red;">{{ errorMessage }}</span>
          </div>
        </v-form>
      </v-card-text>
    </v-card>

    <h2 class="mt-6 mb-3">Your Previous Submissions</h2>
    <div v-if="myFeedback.length === 0" class="text-medium-emphasis">No feedback submitted yet.</div>
    <v-card v-for="item in myFeedback" :key="item.id" class="mb-3">
      <v-card-text>
        <div class="d-flex justify-space-between align-center flex-wrap mb-1" style="gap: 8px;">
          <div>
            <v-chip size="small" class="mr-2" :color="typeColor(item.type)">{{ item.type }}</v-chip>
            <strong>{{ item.subject }}</strong>
          </div>
          <div>
            <v-chip size="small" :color="statusColor(item.status)">{{ item.status }}</v-chip>
            <span class="text-caption text-medium-emphasis ml-2">{{ formatDate(item.submittedAt) }}</span>
          </div>
        </div>
        <div>{{ item.message }}</div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, addDoc, query, where, orderBy, getDocs, serverTimestamp } from 'firebase/firestore';
import { useUserStore } from '@/stores/users';
import { FEEDBACK_DB } from '@/constants/constants';

const userStore = useUserStore();

const formRef = ref(null);
const formValid = ref(false);
const type = ref(null);
const subject = ref('');
const message = ref('');
const submitting = ref(false);
const successMessage = ref('');
const errorMessage = ref('');
const myFeedback = ref([]);

const typeOptions = ['Bug Report', 'Feature Request', 'General Feedback'];

async function submitFeedback() {
  const { valid } = await formRef.value.validate();
  if (!valid) return;

  submitting.value = true;
  successMessage.value = '';
  errorMessage.value = '';

  try {
    await addDoc(collection(db, FEEDBACK_DB), {
      type: type.value,
      subject: subject.value.trim(),
      message: message.value.trim(),
      userId: userStore.user?.id ?? null,
      userAlias: userStore.user?.alias ?? 'Unknown',
      userEmail: userStore.useremail ?? null,
      submittedAt: serverTimestamp(),
      status: 'New',
    });

    successMessage.value = 'Feedback submitted — thank you!';
    type.value = null;
    subject.value = '';
    message.value = '';
    formRef.value.resetValidation();
    await fetchMyFeedback();
  } catch (e) {
    console.error('Error submitting feedback:', e);
    errorMessage.value = 'Error submitting feedback. Please try again.';
  } finally {
    submitting.value = false;
  }
}

async function fetchMyFeedback() {
  const userId = userStore.user?.id;
  if (!userId) return;
  try {
    const q = query(
      collection(db, FEEDBACK_DB),
      where('userId', '==', userId),
      orderBy('submittedAt', 'desc')
    );
    const snap = await getDocs(q);
    myFeedback.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('Error fetching feedback:', e);
  }
}

function formatDate(ts) {
  if (!ts) return '';
  const d = ts.toDate?.() ?? new Date(ts);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function typeColor(t) {
  if (t === 'Bug Report') return 'error';
  if (t === 'Feature Request') return 'primary';
  return 'secondary';
}

function statusColor(s) {
  if (s === 'Resolved') return 'success';
  if (s === 'In Progress') return 'warning';
  return 'default';
}

onMounted(fetchMyFeedback);
</script>
